/* eslint semi: 1 */
import { defineStore } from 'pinia';
import type { AxiosError } from 'axios';
import { proxyException } from '@/utils/communication';
import { Dependencies } from '@/globals/utils';
import type { components } from '@base-shared/types/schedule.openapi';
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore';
import { NotificationAlertItem } from '@base-shared/components/NotificationAlertsItem.vue';

type ScheduleSchema = components['schemas'];

export type Schedule = Omit<ScheduleSchema['ScheduleItem'], 'scheduleTags'>;
export type PagedScheduleResponse = ScheduleSchema['ListSchedulesResponse'];

export type ScheduleQuery = Omit<Partial<components['parameters']>, 'nextToken'> | null;

interface ScheduleV2State {
  schedulePages: PagedScheduleResponse[];
  isFetchingForList: boolean;
  isFetchingNextPage: boolean;
  // Currently displayed page (1-based) within `schedulePages`. Owned by the
  // store so `useScheduleList()` pagination logic and the page component
  // read/write the same value without needing to pass it around.
  currentPage: number;
  // Raw player status string per scheduleId, reported asynchronously by each
  // row's preview iframe via postMessage (see `setSchedulePlayerStatus`).
  // A scheduleId with no entry yet is treated as online (optimistic default).
  playerStatusById: Record<string, string>;

  // Notification payload to be displayed in the notification alert component.
  notification: null | NotificationAlertItem;
}

type FetchPageResult =
  | { success: true; data: PagedScheduleResponse }
  | { success: false; error: AxiosError };

export const useScheduleListStore = defineStore('schedule-list-store', {
  state: (): ScheduleV2State => ({
    schedulePages: [],
    isFetchingForList: false,
    isFetchingNextPage: false,
    currentPage: 1,
    playerStatusById: {},
    notification: null
  }),
  getters: {
    /** Flattened list of every schedule page loaded so far. */
    scheduleItems (state): Schedule[] {
      return state.schedulePages.flatMap((page) => page.items);
    },
    /** Pagination token from the most recently loaded page, or null when there is no further page to load. */
    nextToken (state): string | null {
      return state.schedulePages.at(-1)?.nextToken ?? null;
    },
    /** True when another page can be loaded via `loadNextSchedulePage`. */
    scheduleHasNextPage (): boolean {
      return !!this.nextToken;
    }
  },
  actions: {
    /**
     * Resets all schedule pagination/loading state. Used internally before
     * a fresh `loadSchedulePage` call so stale pages/tokens are never reused.
     */
    resetScheduleState (): void {
      this.$patch({
        schedulePages: [],
        currentPage: 1,
        playerStatusById: {},
      });
    },

    /**
     * Records the raw status string reported by a schedule row's preview iframe
     * (via postMessage). Online/offline derivation is handled by `getPlayerUiState`
     * in the composable so callers can distinguish e.g. 'unconfigured' from 'failed'.
     *
     * @param {string} scheduleId
     * @param {string} rawStatus
     */
    setSchedulePlayerStatus (scheduleId: string, rawStatus: string): void {
      this.playerStatusById = {
        ...this.playerStatusById,
        [scheduleId]: rawStatus,
      };
    },

    /**
     * Loads the initial page of schedules for the schedule page based on the
     * given query params (e.g. start, dur, width, search, tags), replacing any
     * previously loaded pages. Drives the `isFetchingForList` loading
     * state used for the page's loading animation.
     *
     * Completely isolated from the legacy `fetchExtendedSchedules` usage in the
     * old schedule page/search bar - it only shares the low-level HTTP call.
     *
     * @param {ScheduleQuery} params - Query parameters for the initial page (no nextToken).
     * @returns {Promise<FetchPageResult>}
     */
    async loadSchedulePage (params: ScheduleQuery = null): Promise<FetchPageResult> {
      this.resetScheduleState();
      this.$patch({ isFetchingForList: true });

      const result = await scheduleStore().fetchExtendedSchedules(params);

      if (!result.success) {
        this.isFetchingForList = false;
        return result;
      }

      this.schedulePages = [result.data];
      this.isFetchingForList = false;

      return result;
    },

    /**
     * Loads the next page of schedules for the schedule page using the
     * `nextToken` returned by the previous request, appending it to the
     * already loaded pages. No-ops if there is no next token or a next-page
     * fetch is already in flight. Drives `isFetchingNextPage`.
     *
     * @returns {Promise<FetchPageResult | null>} `null` when there is nothing to load.
     */
    async loadNextSchedulePage (query: ScheduleQuery = null): Promise<FetchPageResult | null> {
      if (!this.nextToken || this.isFetchingNextPage) {
        return null;
      }

      this.$patch({ isFetchingNextPage: true });

      const result = await scheduleStore().fetchExtendedSchedules({
        ...query,
        nextToken: this.nextToken,
      });

      if (!result.success) {
        this.isFetchingNextPage = false;
        return result;
      }

      this.schedulePages = [...this.schedulePages, result.data];
      this.isFetchingNextPage = false;

      return result;
    },

    /**
     * Deletes all scheduled media from a given schedule/player, mirroring the
     * legacy schedule page's `handleDeleteAllScheduledMediaFromTarget`.
     *
     * This action only performs the delete request and returns its result.
     * Pagination backfill/re-fetch behavior is orchestrated by the schedule-v2
     * composable so UI-level error handling and retry behavior can be applied
     * independently to delete and fetch phases.
     *
     * @param {string} scheduleId - The schedule/player identifier to delete.
     * @returns {Promise<{ success: true } | { success: false; error: AxiosError }>}
     */
    async deleteSchedule (scheduleId: string): Promise<{ success: true } | { success: false; error: AxiosError }> {
      const $api = Dependencies.inject('$api') as { delete: (url: string) => Promise<unknown> };

      const { error } = await proxyException(() => $api.delete(
        `${CONFIG.API_BASE_URL}/player/${scheduleId}/schedule`
      )) as { ok: unknown; error: AxiosError };

      if (error) {
        return { success: false, error };
      }

      return { success: true };
    },
    setNotification (payload: NotificationAlertItem | null) {
      this.notification = payload;
    }
  },
});

export type ScheduleListStore = ReturnType<typeof useScheduleListStore>;
